import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useJobsSearch } from "@/queries/recruit/use-jobs.query.ts";
import { ApiJobsFilter, ApiJobsFilterDefault, getJobEmoji } from "@/types/recruit/job.types.ts";
import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Select,
  SelectItem,
  Stack,
} from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface ChoiceJobForPositionRequestModalProps extends ModalProps {
  onSelect?: (id: number) => void;
}

export function ChoiceJobForPositionRequestModal({
  onSelect,
  ...props
}: ChoiceJobForPositionRequestModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close } = useModals();

  const [jobsFilter, setJobsFilter] = useState<ApiJobsFilter>(ApiJobsFilterDefault);
  const { data: jobsSearched, isLoading: jobsIsLoading } = useJobsSearch(jobsFilter);

  const JobSchemaObject = z.object({
    job_id: z.number({ error: i18n.t("errors:field_required") }),
  });

  type JobSchema = z.infer<typeof JobSchemaObject>;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<JobSchema>({
    mode: "onSubmit",
    resolver: zodResolver(JobSchemaObject),
  });

  const onSubmit = handleSubmit(async (schema) => {
    if (onSelect) onSelect(schema.job_id);

    close(id);
  });

  async function handleSearchJobs(text: string | undefined) {
    setJobsFilter({
      ...jobsFilter,
      text: text,
    });
  }

  const jobSearchOptions = useMemo(() => {
    if (!jobsSearched) return [];
    return jobsSearched.items.map((job) => ({
      id: job.id,
      title: job.title,
      emoji: getJobEmoji(job),
      caption: job.department?.title,
    })) as SelectItem[];
  }, [jobsSearched]);

  return (
    <Modal
      {...props}
      title={t("hiring_plan:select_job")}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} onClick={onSubmit} size={"lg"}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("ats:job")}
                emptyTitle={t("common:select")}
                isLoading={jobsIsLoading}
                value={value}
                searchable
                onSearchOptions={handleSearchJobs}
                showAvatar={true}
                showCaption={true}
                loadOptionsOnOpen
                searchOptions={jobSearchOptions}
                onChange={onChange}
                error={errors.job_id?.message}
              />
            )}
            control={control}
            name={"job_id"}
          />
        </Stack>
      </form>
    </Modal>
  );
}
